import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Page, Panel, Field } from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import { useCustomers, useSelectedCustomer } from "../lib/hooks";
import { api, notifyErr } from "../lib/api";

const HOURS = Array.from(
  { length: 24 },
  (_, h) => `${String(h).padStart(2, "0")}:00`
);

const DEFAULTS = {
  enabled: false,
  run_time: "03:00",
  lookback_days: 1,
  date_mode: "relative",
  fixed_date_from: "",
  fixed_date_to: "",
  campaigns: [],
  statuses: [],
  all_statuses: false,
  min_duration: 60,
  include_inbound: false,
  auto_analyze: true
};

export default function Programmate() {
  const { customers } = useCustomers();
  const [cid, setCid] = useSelectedCustomer(customers);
  const [customer, setCustomer] = useState(null);
  const [pid, setPid] = useState("");
  const [cfg, setCfg] = useState(DEFAULTS);
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [runs, setRuns] = useState([]);
  const [searchStatus, setSearchStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const change = (key, value) =>
    setCfg(old => ({ ...old, [key]: value }));

  useEffect(() => {
    if (!cid) {
      setCustomer(null);
      setPid("");
      return;
    }

    let active = true;
    setCustomer(null);
    setPid("");

    api.get(`/customers/${cid}`)
      .then(r => {
        if (!active) return;
        setCustomer(r.data);
        setPid(r.data.predictives_list?.[0]?.id || "");
      })
      .catch(e => { if (active) notifyErr(e); });

    return () => { active = false; };
  }, [cid]);

  useEffect(() => {
    if (!pid) return;

    let active = true;
    setLoading(true);
    setCfg(DEFAULTS);
    setSchedules([]);
    setSelectedScheduleId(null);
    setCampaigns([]);
    setStatuses([]);
    setRuns([]);

    Promise.all([
      api.get(`/predictives/${pid}/acquisition-schedules`),
      api.get(`/predictives/${pid}/campaigns`),
      api.get(`/predictives/${pid}/acquisition-schedule/runs`)
    ]).then(([schedule, camps, history]) => {
      if (!active) return;
      const items = Array.isArray(schedule.data)
        ? schedule.data : [];
      setSchedules(items);
      setSelectedScheduleId(items[0]?.id || null);
      setCfg({ ...DEFAULTS, ...(items[0] || {}) });
      setCampaigns(camps.data.items || []);
      setRuns(history.data || []);
    }).catch(e => {
      if (active) notifyErr(e);
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [pid]);

  const campKey = (cfg.campaigns || []).join(",");

  useEffect(() => {
    if (!pid) return;

    let active = true;

    api.get(
      `/predictives/${pid}/statuses?campaigns=${encodeURIComponent(campKey)}`
    ).then(r => {
      if (active) setStatuses(r.data.items || []);
    }).catch(e => {
      if (active) notifyErr(e);
    });

    return () => { active = false; };
  }, [pid, campKey]);

  const toggleCampaign = id => {
    const selected = cfg.campaigns || [];

    change(
      "campaigns",
      selected.includes(id)
        ? selected.filter(x => x !== id)
        : [...selected, id]
    );
  };

  const toggleStatus = code => {
    const selected = cfg.statuses || [];

    change(
      "statuses",
      selected.includes(code)
        ? selected.filter(x => x !== code)
        : [...selected, code]
    );
  };

  const save = async () => {
    if (cfg.date_mode === "fixed") {
      if (!cfg.fixed_date_from || !cfg.fixed_date_to) {
        toast.error("Seleziona entrambe le date");
        return;
      }
      if (cfg.fixed_date_from > cfg.fixed_date_to) {
        toast.error("La data Dal non può superare la data Al");
        return;
      }
    }

    if (cfg.enabled && !cfg.all_statuses && !cfg.statuses.length) {
      toast.error("Seleziona almeno un esito da acquisire");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        enabled: cfg.enabled,
        run_time: cfg.run_time,
        lookback_days: Number(cfg.lookback_days),
        date_mode: cfg.date_mode,
        fixed_date_from: cfg.date_mode === "fixed"
          ? cfg.fixed_date_from : null,
        fixed_date_to: cfg.date_mode === "fixed"
          ? cfg.fixed_date_to : null,
        campaigns: cfg.campaigns,
        statuses: cfg.statuses,
        all_statuses: cfg.all_statuses,
        min_duration: Number(cfg.min_duration),
        include_inbound: cfg.include_inbound,
        auto_analyze: cfg.auto_analyze
      };

      const r = selectedScheduleId
        ? await api.put(
            `/predictives/${pid}/acquisition-schedules/${selectedScheduleId}`,
            payload
          )
        : await api.post(
            `/predictives/${pid}/acquisition-schedules`,
            payload
          );

      setCfg({ ...DEFAULTS, ...r.data });
      setSelectedScheduleId(r.data.id);
      setSchedules(previous => [
        ...previous.filter(item => item.id !== r.data.id),
        r.data
      ].sort((a, b) => a.run_time.localeCompare(b.run_time)));
      toast.success("Programmazione salvata");
    } catch (e) {
      notifyErr(e);
    } finally {
      setSaving(false);
    }
  };

  const refreshRuns = async () => {
    try {
      const r = await api.get(
        `/predictives/${pid}/acquisition-schedule/runs`
      );
      setRuns(r.data || []);
      toast.success("Storico aggiornato");
    } catch (e) {
      notifyErr(e);
    }
  };

  const filteredStatuses = statuses.filter(s =>
    `${s.status} ${s.name || ""}`
      .toLowerCase()
      .includes(searchStatus.toLowerCase())
  );

  return (
    <Page
      title="Acquisizioni Programmate"
      subtitle="Configura l'acquisizione giornaliera automatica delle registrazioni AlFa Dial."
      actions={
        <CustomerSelect
          customers={customers}
          value={cid}
          onChange={setCid}
        />
      }
    >
      {!customer ? (
        <Panel>Seleziona un cliente per configurare le acquisizioni.</Panel>
      ) : (
        <div className="grid xl:grid-cols-[440px_1fr] gap-4">
          <div className="space-y-4">
            <Panel title="Orari programmati">
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={!pid || loading || saving}
                  onClick={() => {
                    const nextHour = HOURS.find(h =>
                      !schedules.some(item => item.run_time === h)
                    ) || "12:00";

                    setSelectedScheduleId(null);
                    setCfg(old => ({
                      ...old,
                      id: null,
                      enabled: false,
                      run_time: nextHour,
                      date_mode: "today",
                      fixed_date_from: "",
                      fixed_date_to: "",
                      last_run_at: null,
                      last_run_local_date: null,
                      last_error: null
                    }));
                  }}
                >
                  + Nuovo orario
                </Button>

                {!schedules.length ? (
                  <p className="text-sm text-neutral-500">
                    Nessuna programmazione salvata.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {schedules.map(item => (
                      <div
                        key={item.id}
                        className="flex flex-wrap items-center justify-between gap-2 border rounded p-3"
                      >
                        <div className="text-sm">
                          <b>{item.run_time}</b>
                          <span className="ml-2 text-neutral-500">
                            {item.enabled ? "Attiva" : "Disattivata"}
                          </span>
                          <div className="text-xs text-neutral-500">
                            {item.date_mode === "today"
                              ? "Oggi"
                              : item.date_mode === "fixed"
                                ? "Date personalizzate"
                                : "Giorni precedenti"}
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedScheduleId === item.id
                            ? "default" : "outline"}
                          disabled={saving}
                          onClick={() => {
                            setSelectedScheduleId(item.id);
                            setCfg({ ...DEFAULTS, ...item });
                          }}
                        >
                          {selectedScheduleId === item.id
                            ? "Selezionata" : "Modifica"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-xs text-neutral-500">
                  Seleziona un orario per modificarlo o disattivarlo
                  usando il modulo sottostante.
                </p>
              </div>
            </Panel>

            <Panel title="Pianificazione giornaliera">
              <div className="space-y-4">
                <Field label="Predictive">
                  <Select value={pid} onValueChange={setPid}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona predictive" />
                    </SelectTrigger>
                    <SelectContent>
                      {(customer.predictives_list || []).map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <label className="flex items-center justify-between gap-3 border rounded p-3">
                  <span className="text-sm font-medium">
                    Acquisizione automatica attiva
                  </span>
                  <Switch
                    checked={cfg.enabled}
                    onCheckedChange={v => change("enabled", v)}
                  />
                </label>

                <Field label="Orario giornaliero">
                  <Select
                    value={cfg.run_time}
                    onValueChange={v => change("run_time", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Modalità acquisizione">
                  <Select
                    value={cfg.date_mode || "relative"}
                    onValueChange={v => change("date_mode", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relative">
                        Giornaliera automatica
                      </SelectItem>
                      <SelectItem value="today">
                        Oggi - registrazioni della giornata
                      </SelectItem>
                      <SelectItem value="fixed">
                        Intervallo date personalizzato
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

                {cfg.date_mode === "fixed" ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Dal giorno">
                        <Input
                          type="date"
                          value={cfg.fixed_date_from || ""}
                          onChange={e =>
                            change("fixed_date_from", e.target.value)
                          }
                        />
                      </Field>
                      <Field label="Al giorno">
                        <Input
                          type="date"
                          value={cfg.fixed_date_to || ""}
                          onChange={e =>
                            change("fixed_date_to", e.target.value)
                          }
                        />
                      </Field>
                    </div>
                    <p className="text-xs text-neutral-500">
                      Intervallo consentito: ultimi 5 giorni.
                      La pianificazione si disattiva dopo
                      l'accodamento del lavoro.
                    </p>
                  </div>
                ) : cfg.date_mode === "today" ? (
                  <p className="text-xs text-neutral-500">
                    Acquisisce solo le registrazioni di oggi
                    gia terminate e disponibili.
                  </p>
                ) : (
                  <Field label="Periodo delle registrazioni">
                    <Select
                      value={String(cfg.lookback_days)}
                      onValueChange={v =>
                        change("lookback_days", Number(v))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">
                          Giorno precedente
                        </SelectItem>
                        <SelectItem value="2">
                          Ultimi 2 giorni completi
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                )}

                <Field label="Durata minima (secondi)">
                  <Input
                    type="number"
                    min="0"
                    value={cfg.min_duration}
                    onChange={e =>
                      change("min_duration", e.target.value)
                    }
                  />
                </Field>

                <label className="flex items-center justify-between gap-3">
                  <span className="text-sm">Includi chiamate inbound</span>
                  <Switch
                    checked={cfg.include_inbound}
                    onCheckedChange={v => change("include_inbound", v)}
                  />
                </label>

                <label className="flex items-center justify-between gap-3">
                  <span className="text-sm">
                    Analisi automatica Whisper + Qwen
                  </span>
                  <Switch
                    checked={cfg.auto_analyze}
                    onCheckedChange={v => change("auto_analyze", v)}
                  />
                </label>
              </div>
            </Panel>

            <Panel title="Campagne">
              <p className="text-xs text-neutral-500 mb-2">
                Nessuna selezionata = tutte le campagne.
              </p>
              <div className="max-h-48 overflow-y-auto divide-y border rounded">
                {campaigns.map(c => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 p-2 text-xs cursor-pointer"
                  >
                    <Checkbox
                      checked={cfg.campaigns.includes(c.id)}
                      onCheckedChange={() => toggleCampaign(c.id)}
                    />
                    <span className="font-mono">{c.id}</span>
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </Panel>

            <Panel title="Esiti da acquisire">
              <label className="flex items-center gap-2 mb-3 text-xs font-medium">
                <Checkbox
                  checked={cfg.all_statuses}
                  onCheckedChange={v => change("all_statuses", !!v)}
                />
                TUTTI GLI ESITI
              </label>

              <Input
                placeholder="Cerca esito o descrizione"
                value={searchStatus}
                onChange={e => setSearchStatus(e.target.value)}
                disabled={cfg.all_statuses}
              />

              <div className="flex gap-2 my-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cfg.all_statuses}
                  onClick={() =>
                    change("statuses", [
                      ...new Set([
                        ...cfg.statuses,
                        ...filteredStatuses.map(s => s.status)
                      ])
                    ])
                  }
                >
                  Seleziona visibili
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cfg.all_statuses}
                  onClick={() => change("statuses", [])}
                >
                  Deseleziona
                </Button>
              </div>

              <div className="border rounded max-h-64 overflow-y-auto divide-y">
                {filteredStatuses.map(s => (
                  <label
                    key={s.status}
                    className="flex gap-2 items-center p-2 text-xs cursor-pointer"
                  >
                    <Checkbox
                      checked={cfg.statuses.includes(s.status)}
                      disabled={cfg.all_statuses}
                      onCheckedChange={() => toggleStatus(s.status)}
                    />
                    <span className="font-mono w-14">
                      {s.status}
                    </span>
                    <span>{s.name || "—"}</span>
                  </label>
                ))}
              </div>

              <p className="text-xs text-neutral-500 mt-2">
                {cfg.all_statuses
                  ? "Tutti gli esiti inclusi"
                  : `${cfg.statuses.length} esiti selezionati`}
              </p>
            </Panel>

            <Button
              className="w-full bg-[#0047FF]"
              disabled={!pid || loading || saving}
              onClick={save}
            >
              {saving ? "Salvataggio..." : "Salva programmazione"}
            </Button>
          </div>

          <div className="space-y-4">
            <Panel title="Stato programmazione">
              <div className="space-y-2 text-sm">
                <div>
                  <b>Stato:</b>{" "}
                  {cfg.enabled ? "Attiva" : "Disattivata"}
                </div>
                <div>
                  <b>Orario:</b> {cfg.run_time}
                </div>
                <div>
                  <b>Ultima esecuzione:</b>{" "}
                  {cfg.last_run_at
                    ? new Date(cfg.last_run_at).toLocaleString("it-IT")
                    : "Mai eseguita"}
                </div>
                {cfg.last_error && (
                  <p className="text-red-700">
                    Ultimo errore: {cfg.last_error}
                  </p>
                )}
                <p className="text-xs text-neutral-500">
                  Le registrazioni originali sul predictive non vengono
                  cancellate o modificate.
                </p>
              </div>
            </Panel>

            <Panel
              title="Storico acquisizioni automatiche"
              actions={
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!pid}
                  onClick={refreshRuns}
                >
                  Aggiorna
                </Button>
              }
            >
              {!runs.length ? (
                <p className="text-sm text-neutral-500">
                  Nessuna esecuzione automatica registrata.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="p-2">Data</th>
                        <th className="p-2">Orario</th>
                        <th className="p-2">Stato</th>
                        <th className="p-2">Trovate</th>
                        <th className="p-2">Accodate</th>
                        <th className="p-2">Dettaglio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map(r => (
                        <tr key={r.id} className="border-b">
                          <td className="p-2">{r.local_date}</td>
                          <td className="p-2">{r.run_time || "—"}</td>
                          <td className="p-2">{r.status}</td>
                          <td className="p-2">{r.found}</td>
                          <td className="p-2">{r.acquired}</td>
                          <td className="p-2">{r.detail || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}
    </Page>
  );
}
